import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOutIcon,
  ChevronDownIcon,
  PlusIcon,
  CameraIcon,
  SettingsIcon,
  KeyIcon,
  TrashIcon,
  CheckCircleIcon,
  CheckIcon,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { UserContext } from "../contexts/UserContext";
import StorageService from "../services/storageService";
import { useLogoutCleanup } from "../hooks/useLogoutCleanup";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

/* Temporarily disabled until goals are needed again.
const goalLabels = [
  "School Exams",
  "IIT JEE Mains",
  "IIT JEE Advanced",
  "NEET",
  "CUET",
  "Olympiad",
  "NTSE",
  "SAT",
];
*/

const metadata = {
  grades: [
    "1st Grade",
    "2nd Grade",
    "3rd Grade",
    "4th Grade",
    "5th Grade",
    "6th Grade",
    "7th Grade",
    "8th Grade",
    "9th Grade",
    "10th Grade",
    "11th Grade",
    "12th Grade",
  ],
  /*
  cities: [
    "New Delhi",
    "Faridabad",
    "Amritsar",
    "Bathinda",
    "Kurukshetra",
    "Noida",
    "Patiala",
    "Gurugram",
    "Panipat",
    "Palwal",
  ],
  */
  boards: ["CBSE", "ICSE", "IB", "IGCSE", "Cambridge"],
  schools: {
    "New Delhi": [
      "Green Land Public School",
      "Geeta Bal Bharti Sr. Sec. School",
      "Puneet Public School",
      "St. Josephs Academy",
      "DAV Public School",
    ],
    Faridabad: ["The Shriram Millennium Schools Faridabad"],
    Amritsar: ["The Millennium Schools Amritsar"],
    Bathinda: [
      "The Millennium Schools Bathinda",
      "The Millennium Schools HMEL Bathinda",
    ],
    Kurukshetra: ["The Millennium Schools Kurukshetra"],
    Noida: [
      "The Millennium Schools Noida",
      "The Shriram Millennium Schools Faridabad",
    ],
    Patiala: ["The Millennium Schools Patiala"],
    Gurugram: [
      "The Millennium Schools Gurgaon",
      "The Shriram Millennium Schools Gurugram",
    ],
    Panipat: ["The Millennium Schools Panipat"],
    Palwal: ["The Millennium Schools Palwal"],
  },
  /*
  goals: goalLabels.map((label) => ({
    label,
    value: label.toLowerCase(),
  })),
  */
};

export default function Profile() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    school: "",
    // city: "",
    board: "",
    grade: "",
    emailId: "",
    contactNumber: "",
    // pincode: "",
    profilePhoto: "",
    // goals: [],
  });
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  const [dropdownStates, setDropdownStates] = useState({
    grade: false,
    school: false,
    board: false,
    settings: false,
  });

  const [searchTerms, setSearchTerms] = useState({
    grade: "",
    school: "",
    board: "",
  });

  const [error, setError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const fileInputRef = useRef(null);
  const dropdownRefs = useRef({});
  const settingsRef = useRef(null);

  const { updateProfilePhoto, updateProfileDetails } = useContext(UserContext);
  const { performLogoutCleanup } = useLogoutCleanup();

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const normalizeBoardValue = (boardValue) => {
    if (!boardValue) return "";
    // Find matching board from master data (case-insensitive)
    const matchedBoard = metadata.boards.find(
      (board) => board.toLowerCase() === boardValue.toLowerCase()
    );
    return matchedBoard || boardValue;
  };

  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    return String(value).trim().length > 0;
  };

  useEffect(() => {
    const fetchUser = async () => {
      setIsProfileLoading(true);
      try {
        const token = StorageService.getItem("token");
        if (!token) {
          throw new Error("You need to log in to view profile.");
        }

        const response = await axios.get(`${baseUrl}/v2/profile/user`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const {
          fullName,
          email,
          school,
          board,
          grade,
          contactNumber,
          profilePhoto,
        } = response.data;

        const normalizedData = {
          name: capitalizeWords(fullName?.trim() || ""),
          emailId: email || "",
          school: capitalizeWords(school || ""),
          board: normalizeBoardValue(board || ""),
          grade: grade || "",
          contactNumber: contactNumber || "",
          profilePhoto: profilePhoto || "",
        };

        setFormData((prev) => ({
          ...prev,
          ...normalizedData,
        }));

        updateProfileDetails({
          name: normalizedData.name,
          email: normalizedData.emailId,
          school: normalizedData.school,
          board: normalizedData.board,
          grade: normalizedData.grade,
          contactNumber: normalizedData.contactNumber,
          profilePhoto: normalizedData.profilePhoto,
        });

        const requiredFieldValues = {
          board: normalizedData.board,
          grade: normalizedData.grade,
          email: normalizedData.emailId,
          contactNumber: normalizedData.contactNumber,
        };
        const isProfileComplete =
          Object.values(requiredFieldValues).every(hasValue);
        if (isProfileComplete) {
          StorageService.setItem("profileUpdated", "true");
        } else {
          // Check if we've already shown this toast to prevent duplicates
          const hasShownProfileToast =
            sessionStorage.getItem("profileToastShown");
          if (!hasShownProfileToast) {
            toast.warn("Please complete your profile to continue.");
            sessionStorage.setItem("profileToastShown", "true");
          }
        }
        setError(null);
      } catch (error) {
        console.error(
          "Error fetching user:",
          error.response?.data || error.message
        );
        setError("Failed to fetch user data. Please ensure you are logged in.");
      }
      setIsProfileLoading(false);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach((key) => {
        if (
          dropdownRefs.current[key] &&
          !dropdownRefs.current[key].contains(event.target)
        ) {
          setDropdownStates((prev) => ({ ...prev, [key]: false }));
        }
      });
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setDropdownStates((prev) => ({ ...prev, settings: false }));
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Cleanup effect to clear session storage when component unmounts
  useEffect(() => {
    return () => {
      // Clear the profile toast flag when component unmounts
      sessionStorage.removeItem("profileToastShown");
    };
  }, []);

  const handleInputChange = (field, value) => {
    const processedValue = ["emailId", "contactNumber"].includes(field)
      ? value
      : capitalizeWords(value);
    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handleDropdownToggle = (field) => (event) => {
    event.stopPropagation();
    setDropdownStates((prev) => ({
      ...prev,
      grade: false,
      school: false,
      board: false,
      settings: false,
      [field]: !prev[field],
    }));
    setSearchTerms((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDropdownSelect = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "board" ? value : capitalizeWords(value),
    }));
    setDropdownStates((prev) => ({ ...prev, [field]: false }));
    setSearchTerms((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSearchChange = (field, value) => {
    setSearchTerms((prev) => ({ ...prev, [field]: value }));
  };

  const getFilteredOptions = (field) => {
    const searchTerm = searchTerms[field].toLowerCase();
    let options = [];
    switch (field) {
      case "grade":
        options = metadata.grades;
        break;
      case "school":
        options = Object.values(metadata.schools).flat();
        break;
      case "board":
        options = metadata.boards;
        break;
      default:
        options = [];
    }
    return options.filter((option) =>
      option.toLowerCase().includes(searchTerm)
    );
  };

  const handleAddCustom = (field, value) => {
    if (value.trim()) {
      handleDropdownSelect(field, value.trim());
    }
  };

  const handleMultiSelectOption = (field, optionValue) => {
    setFormData((prev) => {
      const currentValues = Array.isArray(prev[field]) ? prev[field] : [];
      const isSelected = currentValues.includes(optionValue);
      const updatedValues = isSelected
        ? currentValues.filter((value) => value !== optionValue)
        : [...currentValues, optionValue];
      return {
        ...prev,
        [field]: updatedValues,
      };
    });
    setDropdownStates((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleClearMultiSelect = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [],
    }));
    setSearchTerms((prev) => ({ ...prev, [field]: "" }));
    setDropdownStates((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.info("Please upload a JPEG or PNG image.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.warn("Image size must be less than 2MB.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = StorageService.getItem("token");
      if (!token) {
        throw new Error("You need to log in to upload a profile photo.");
      }

      const response = await axios.post(
        `${baseUrl}/v2/profile/upload-photo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setFormData((prev) => ({
        ...prev,
        profilePhoto: response.data.profilePhoto,
      }));
      toast.success("Profile photo uploaded successfully!");
      updateProfilePhoto(response.data.profilePhoto);
    } catch (error) {
      console.error(
        "Error uploading photo:",
        error.response?.data || error.message
      );
      toast.error("Failed to upload profile photo. Please try again.");
    }
  };

  const handleCameraClick = () => {
    fileInputRef.current.click();
  };

  const handleSave = async () => {
    const requiredFields = ["board", "grade", "emailId", "contactNumber"];
    if (!requiredFields.every((field) => formData[field].trim())) {
      toast.warn("Please fill all required fields.");
      return;
    }

    const dataToSave = {
      fullName: formData.name,
      email: formData.emailId,
      school: formData.school,
      board: capitalizeWords(formData.board),
      grade: formData.grade,
      contactNumber: formData.contactNumber,
    };

    try {
      const token = StorageService.getItem("token");
      if (!token) {
        throw new Error("You need to log in to save profile.");
      }

      const response = await axios.put(
        `${baseUrl}/v2/profile/update-profile`,
        dataToSave,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const newToken = response?.data?.token;
      if (newToken && typeof newToken === "string") {
        StorageService.setItem("token", newToken);
        try {
          localStorage.setItem("token", newToken);
        } catch (storageError) {
          console.error("Unable to persist new auth token:", storageError);
        }
      }

      const updatedFormData = {
        name: capitalizeWords(response.data.fullName?.trim() || ""),
        emailId: response.data.email || "",
        school: capitalizeWords(response.data.school || ""),
        board: normalizeBoardValue(response.data.board || ""),
        grade: response.data.grade || "",
        contactNumber: response.data.contactNumber || "",
        profilePhoto: response.data.profilePhoto || "",
      };

      setFormData((prev) => ({
        ...prev,
        ...updatedFormData,
      }));

      updateProfileDetails({
        name: updatedFormData.name,
        email: updatedFormData.emailId,
        school: updatedFormData.school,
        board: updatedFormData.board,
        grade: updatedFormData.grade,
        contactNumber: updatedFormData.contactNumber,
        profilePhoto: updatedFormData.profilePhoto,
      });

      StorageService.setItem("profileUpdated", "true");
      // Clear the profile toast flag since profile is now complete
      sessionStorage.removeItem("profileToastShown");
      toast.success("Profile saved successfully!");
      navigate("/");
    } catch (error) {
      console.error(
        "Error saving profile:",
        error.response?.data || error.message
      );
      toast.error(
        error.response?.data?.detail ||
          "Failed to save profile. Please try again."
      );
    }
  };

  const handleChangePassword = async () => {
    if (
      !passwordData.oldPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmNewPassword
    ) {
      toast.info("Please fill in all password fields.");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.warn("New password must be at least 8 characters.");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      toast.warn("New password and confirm password do not match.");
      return;
    }

    try {
      const token = StorageService.getItem("token");
      if (!token) {
        throw new Error("You need to log in to change password.");
      }

      await axios.post(
        `${baseUrl}/v2/profile/change-password`,
        {
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Password changed successfully!");
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setShowPasswordModal(false);
    } catch (error) {
      console.error(
        "Error changing password:",
        error.response?.data || error.message
      );
      toast.error(
        error.response?.data?.detail ||
          "Failed to change password. Please try again."
      );
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const token = StorageService.getItem("token");
      if (!token) {
        throw new Error("You need to log in to delete account.");
      }

      await axios.delete(`${baseUrl}/v2/profile/delete-account`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Account deleted successfully!");
      const redirectPath = await performLogoutCleanup({
        hardRefresh: true,
        redirectPath: "/login",
      });
      if (redirectPath) {
        navigate(redirectPath);
      }
    } catch (error) {
      console.error(
        "Error deleting account:",
        error.response?.data || error.message
      );
      toast.error(
        error.response?.data?.detail ||
          "Failed to delete account. Please try again."
      );
    }
  };

  const handleLogout = async () => {
    toast.success("Logged out successfully!");
    const redirectPath = await performLogoutCleanup({
      hardRefresh: true,
      redirectPath: "/login",
    });
    if (redirectPath) {
      navigate(redirectPath);
    }
  };

  const DropdownField = ({
    field,
    label,
    value,
    placeholder,
    disabled = false,
  }) => {
    const filteredOptions = getFilteredOptions(field);
    const searchTerm = searchTerms[field];

    return (
      <div
        className="relative"
        ref={(el) => (dropdownRefs.current[field] = el)}
      >
        <label className="block text-[#0D99FF] text-lg font-medium mb-2">
          {label}
        </label>
        <div
          className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all flex items-center justify-between bg-white ${
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          onClick={disabled ? null : handleDropdownToggle(field)}
        >
          <span className={value ? "text-gray-900" : "text-gray-400"}>
            {value || placeholder}
          </span>
          <ChevronDownIcon
            size={20}
            className={`text-gray-400 transition-transform ${
              dropdownStates[field] ? "rotate-180" : ""
            }`}
          />
        </div>
        {dropdownStates[field] && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => handleSearchChange(field, e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                autoFocus
              />
            </div>
            <div className="max-h-32 overflow-y-auto">
              {filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className="px-4 py-2 hover:bg-purple-50 cursor-pointer text-gray-700"
                  onClick={() => handleDropdownSelect(field, option)}
                >
                  {option}
                </div>
              ))}
              {searchTerm && !filteredOptions.includes(searchTerm) && (
                <div
                  className="px-4 py-2 hover:bg-purple-50 cursor-pointer text-purple-600 flex items-center gap-2 border-t border-gray-200"
                  onClick={() => handleAddCustom(field, searchTerm)}
                >
                  <PlusIcon size={16} />
                  Add "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const MultiSelectField = ({
    field,
    label,
    placeholder,
    options = [],
    disabled = false,
  }) => {
    const selectedValues = Array.isArray(formData[field])
      ? formData[field]
      : [];
    const searchTerm = searchTerms[field];
    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const selectedLabels = selectedValues
      .map((value) => {
        const match = options.find((option) => option.value === value);
        return match ? match.label : null;
      })
      .filter(Boolean);

    return (
      <div
        className="relative"
        ref={(el) => (dropdownRefs.current[field] = el)}
      >
        <label className="block text-[#0D99FF] text-lg font-medium mb-2">
          {label}
        </label>
        <div
          className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all flex items-center justify-between gap-2 bg-white ${
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          onClick={disabled ? null : handleDropdownToggle(field)}
        >
          <span
            className={`flex-1 truncate ${
              selectedLabels.length ? "text-gray-900" : "text-gray-400"
            }`}
          >
            {selectedLabels.length ? selectedLabels.join(", ") : placeholder}
          </span>
          <ChevronDownIcon
            size={20}
            className={`text-gray-400 transition-transform ${
              dropdownStates[field] ? "rotate-180" : ""
            }`}
          />
        </div>
        {dropdownStates[field] && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => handleSearchChange(field, e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                autoFocus
              />
            </div>
            <div className="max-h-30 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      className={`px-4 py-2 flex items-center gap-3 text-sm cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-purple-50 text-purple-700"
                          : "hover:bg-purple-50 text-gray-700"
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMultiSelectOption(field, option.value);
                      }}
                    >
                      <span
                        className={`w-4 h-4 flex items-center justify-center border rounded ${
                          isSelected
                            ? "bg-[#0D99FF] border-[#0D99FF] text-white"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {isSelected && <CheckIcon size={12} />}
                      </span>
                      <span>{option.label}</span>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No results found.
                </div>
              )}
            </div>
            {selectedValues.length > 0 && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClearMultiSelect(field);
                }}
                className="w-full px-4 py-2 text-sm text-left text-purple-600 border-t border-gray-200 hover:bg-purple-50"
              >
                Clear selection
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (error) {
    return (
      <div
        className="min-h-screen p-6 flex items-center justify-center"
        style={{ backgroundColor: "rgb(227, 225, 246)" }}
      >
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <h2 className="text-2xl text-red-600 mb-4">Error</h2>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ backgroundColor: "rgb(227, 225, 246)" }}
      className="w-full min-h-screen px-4 py-6 overflow-visible"
    >
      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-medium text-gray-800 mb-4">
              Change Password
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[#0D99FF] text-sm font-medium mb-1">
                  Old Password
                </label>
                <input
                  type="password"
                  value={passwordData.oldPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      oldPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="Enter old password"
                />
              </div>
              <div>
                <label className="block text-[#0D99FF] text-sm font-medium mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-[#0D99FF] text-sm font-medium mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmNewPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      confirmNewPassword: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    oldPassword: "",
                    newPassword: "",
                    confirmNewPassword: "",
                  });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                className="px-4 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-purple-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-medium text-gray-800 mb-4">
              Delete Account
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your account? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteAccount();
                  setShowDeleteConfirm(false);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 relative md:h-40 bg-[url('/profileBanner.png')] bg-cover bg-center rounded-2xl overflow-visible shadow-lg">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 px-6 py-6 md:px-8 md:py-0 md:h-full">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:gap-8 order-last md:order-none w-full">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white p-1 relative mx-auto sm:mx-0">
              <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <img
                  className="w-20 h-20 md:w-28 md:h-28 rounded-full object-cover"
                  src={formData.profilePhoto || "/assets/avatars/avatar1.png"}
                  alt="profile"
                />
                <div
                  className="absolute -bottom-1 -right-1 md:bottom-1 md:right-1 bg-white p-1 rounded-full shadow-md cursor-pointer"
                  onClick={handleCameraClick}
                >
                  <CameraIcon className="w-4 h-4 text-black" />
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/jpeg,image/png"
                  className="hidden"
                />
              </div>
            </div>
            <div className="text-center sm:text-left">
              <h1 className="text-gray-100 text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-wide drop-shadow-md">
                {formData.name}
              </h1>
            </div>
          </div>
          <div
            className="relative flex justify-end md:block w-full md:w-auto order-first md:order-none"
            ref={settingsRef}
          >
            <button
              onClick={handleDropdownToggle("settings")}
              className="p-2 md:p-3 bg-purple-50 border-purple-300 rounded-full shadow-lg hover:bg-purple-100 hover:scale-110 transition-all duration-200"
            >
              <SettingsIcon className="w-7 h-7 md:w-10 md:h-10 text-purple-600" />
            </button>
            {dropdownStates.settings && (
              <div className="absolute right-0 top-full mt-2 md:mt-0 w-56 bg-white border border-gray-300 rounded-xl shadow-xl z-50 overflow-hidden transition-all duration-200">
                <div className="py-2">
                  <div
                    className="px-4 py-3 hover:bg-purple-100 cursor-pointer text-gray-800 flex items-center gap-3 text-sm font-medium transition-colors"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    <KeyIcon size={16} className="text-gray-600" />
                    Update Password
                  </div>
                  <div
                    className="px-4 py-3 hover:bg-purple-100 cursor-pointer text-gray-800 flex items-center gap-3 text-sm font-medium transition-colors"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <TrashIcon size={16} className="text-gray-600" />
                    Delete Account
                  </div>
                  <div
                    className="px-4 py-3 hover:bg-purple-100 cursor-pointer text-gray-800 flex items-center gap-3 text-sm font-medium transition-colors"
                    onClick={handleLogout}
                  >
                    <LogOutIcon size={16} className="text-gray-600" />
                    Log Out
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full overflow-visible">
        {isProfileLoading ? (
          <div className="py-16 flex items-center justify-center">
            <SimpleThreeDotsLoader message="Loading your profile details..." />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-[#0D99FF] text-lg font-medium mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-[#0D99FF] text-lg font-medium mb-2">
                  Email Id
                </label>
                <div className="relative">
                  <input
                    type="email"
                    disabled
                    value={formData.emailId}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none"
                    placeholder="Verified email"
                  />
                  <CheckCircleIcon
                    size={20}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-green-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[#0D99FF] text-lg font-medium mb-2">
                  Contact Number
                </label>
                <input
                  type="tel"
                  value={formData.contactNumber}
                  onChange={(e) =>
                    handleInputChange("contactNumber", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                  placeholder="Enter contact number"
                />
              </div>
              {/* Pincode temporarily disabled */}
              {/*
              <div>
                <label className="block text-[#0D99FF] text-lg font-medium mb-2">
                  Pincode
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={formData.pincode}
                  onChange={(e) =>
                    handleInputChange(
                      "pincode",
                      e.target.value.replace(/[^0-9]/g, "")
                    )
                  }
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                  placeholder="Enter pincode"
                />
              </div>
              */}
              <div>
                <DropdownField
                  field="grade"
                  label="Grade"
                  value={formData.grade}
                  placeholder="Select Grade"
                />
              </div>
              {/* City temporarily disabled */}
              {/*
              <div>
                <DropdownField
                  field="city"
                  label="City"
                  value={formData.city}
                  placeholder="Select City"
                />
              </div>
              */}
              <div>
                <DropdownField
                  field="school"
                  label="School"
                  value={formData.school}
                  placeholder="Select School"
                />
              </div>
              <div>
                <DropdownField
                  field="board"
                  label="Board"
                  value={formData.board}
                  placeholder="Select Board"
                />
              </div>
              {/* Goals temporarily disabled */}
              {/*
              <div>
                <MultiSelectField
                  field="goals"
                  label="Goals"
                  placeholder="Select Goals"
                  options={metadata.goals}
                />
              </div>
              */}
            </div>
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSave}
                className="px-8 py-3 bg-[#0D99FF] text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Save Profile
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
